import classNames from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  CSSProperties,
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
  RefObject,
} from 'react';
import { createPortal } from 'react-dom';

import { useAnchoredPosition } from '../../hooks/useAnchoredPosition';
import type { AnchoredPlacement } from '../../hooks/useAnchoredPosition';
import { useBreakpoint } from '../../tokens/breakpoints';

import styles from './DropdownMenu.module.css';

export interface DropdownMenuProps {
  /** Element the menu is anchored to (the trigger). */
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  /** Called when the menu should close (outside click / Escape / overlay). */
  onClose: () => void;
  children: ReactNode;
  placement?: AnchoredPlacement;
  /** Match the panel width to the anchor's. */
  matchWidth?: boolean;
  /**
   * ARIA role of the panel's content. `listbox` and `menu` are composite widgets and constrain
   * what may be inside them to `option` / `menuitem` children. Use `dialog` for a panel that holds
   * a form — fields, checkboxes, a Select — because `aria-required-children` fails otherwise: the
   * role then lands on the panel itself and the scroll container carries no role at all.
   */
  role?: 'listbox' | 'menu' | 'dialog';
  id?: string;
  /** Fixed above the scroll area (e.g. a search field). */
  header?: ReactNode;
  /** Fixed below the scroll area (e.g. Reset / Select all). */
  footer?: ReactNode;
  /** Max height of the scroll area, in px (anchored dropdown only). */
  maxHeight?: number;
  /**
   * On small screens, present the menu as a bottom sheet (slides up over a dismissable overlay)
   * instead of an anchored dropdown. Defaults to `true` for `menu` and `dialog`, `false` for
   * `listbox` — a listbox belongs to the combobox that owns it, wherever that is presented.
   */
  mobileSheet?: boolean;
  className?: string;
  'aria-label'?: string | undefined;
  'aria-labelledby'?: string | undefined;
  'aria-activedescendant'?: string | undefined;
  'aria-multiselectable'?: boolean | undefined;
  /** Ref to the scroll list (so the parent can scroll the active item into view). */
  listRef?: RefObject<HTMLDivElement | null>;
}

const DEFAULT_MAX_HEIGHT = 320;
/** Kept in sync with the sheet exit keyframe in `DropdownMenu.module.css`. */
const SHEET_EXIT_MS = 280;

type SheetPhase = 'closed' | 'open' | 'closing';

/**
 * Every panel currently on screen, in the order they opened.
 *
 * A panel opened from inside another one — a `Select` listbox inside a filter popup, say — is its
 * own portal under `<body>`, so the outer panel's `contains(target)` check cannot see it: clicking
 * an option in the inner listbox read as an outside click and closed the popup hosting it. The
 * stack lets a panel recognise a click that landed in a panel opened *after* it as its own, and
 * lets Escape close only the newest panel rather than every open one at once.
 */
const openPanels: HTMLElement[] = [];

/**
 * Anchored, viewport-aware popover panel rendered in a portal: flips/shifts to stay on
 * screen, closes on outside click and Escape, and hosts an optional header/footer around
 * a scrollable list. Keyboard navigation of the items is owned by the trigger (combobox).
 *
 * On small screens it can instead present as a bottom sheet — sliding up over a dismissable
 * overlay with a spring, hiding back down on close (see `mobileSheet`). Insert `MenuSeparator`
 * children to divide item groups in either presentation.
 *
 * @interim Seeded from the Figma "Dropdown menu"; replaceable by the UI Kit.
 */
export function DropdownMenu({
  anchorRef,
  open,
  onClose,
  children,
  placement = 'bottom-start',
  matchWidth = true,
  role = 'listbox',
  id,
  header,
  footer,
  maxHeight = DEFAULT_MAX_HEIGHT,
  mobileSheet,
  className,
  listRef,
  ...aria
}: DropdownMenuProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const breakpoint = useBreakpoint();
  const position = useAnchoredPosition({
    open,
    anchorRef,
    floatingRef: panelRef,
    placement,
    matchWidth,
  });

  const asSheet = (mobileSheet ?? role !== 'listbox') && breakpoint === 'sm';
  const asDialog = role === 'dialog';

  // The sheet stays mounted through its slide-down so the exit animation can play.
  const [phase, setPhase] = useState<SheetPhase>(open ? 'open' : 'closed');
  useEffect(() => {
    setPhase((prev) => (open ? 'open' : prev === 'closed' ? 'closed' : 'closing'));
  }, [open]);
  useEffect(() => {
    if (phase !== 'closing') return undefined;
    const timer = setTimeout(() => {
      setPhase('closed');
    }, SHEET_EXIT_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [phase]);

  const sheetVisible = asSheet && phase !== 'closed';
  const dropdownVisible = !asSheet && open;

  // Join the stack while this panel is on screen. Declared before the dismissal effect below so
  // that within one commit the panel is registered by the time that effect reads the stack.
  useEffect(() => {
    const panel = panelRef.current;
    if (!(sheetVisible || dropdownVisible) || panel == null) return undefined;
    openPanels.push(panel);
    return () => {
      const index = openPanels.indexOf(panel);
      if (index !== -1) openPanels.splice(index, 1);
    };
  }, [sheetVisible, dropdownVisible]);

  /**
   * Put focus back on the control that opened the panel.
   *
   * WCAG 2.4.3: dismissing a panel must not drop focus onto `<body>`, which would restart tabbing
   * from the top of the document.
   */
  const returnFocus = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const selector = 'button, a, input, select, textarea, [tabindex]';
    const focusable = anchor.matches(selector)
      ? anchor
      : anchor.querySelector<HTMLElement>(selector);
    focusable?.focus();
  }, [anchorRef]);

  // Escape closes both presentations; outside-click only applies to the anchored dropdown
  // (the sheet is dismissed by its overlay).
  useEffect(() => {
    if (!open) return undefined;
    /** Panels opened after this one — nested UI of ours, siblings only because both are portaled. */
    const nested = (): HTMLElement[] => {
      const panel = panelRef.current;
      if (panel == null) return [];
      const self = openPanels.indexOf(panel);
      return self === -1 ? [] : openPanels.slice(self + 1);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      // Queue-close, newest first: Escape inside a nested Select closes that listbox and leaves the
      // panel hosting it open. Without this every open panel closed on one key press.
      if (event.key !== 'Escape' || nested().length > 0) return;
      // Focus restoration is not decided here: the effect below watches every close, so an Escape
      // pressed elsewhere on the page leaves the caret where the user actually is.
      onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    if (asSheet) {
      return () => {
        document.removeEventListener('keydown', onKeyDown);
      };
    }
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target) || anchorRef.current?.contains(target)) return;
      if (nested().some((panel) => panel.contains(target))) return;
      onClose();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open, onClose, anchorRef, asSheet]);

  /**
   * One rule for every way a panel can close, rather than one per dismissal path.
   *
   * Escape used to be the only path that restored focus, so the commonest keyboard route of all —
   * Tab to a footer button, Enter — unmounted the panel while focus sat on that button and left it
   * on `<body>`. So did an outside click, the sheet overlay, and any close the consumer performs
   * itself (`Apply`, `Clear`, a close button). This effect runs after the panel is gone, which is
   * exactly what makes it able to tell the two cases apart: focus is on `<body>` only when the
   * element holding it has just been removed, so restoring is right; anywhere else, the user has
   * moved on and their caret is left alone.
   */
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open) {
      wasOpen.current = true;
      return;
    }
    if (!wasOpen.current) return;
    wasOpen.current = false;
    const active = document.activeElement;
    if (active === null || active === document.body) returnFocus();
  }, [open, returnFocus]);

  // Lock page scroll behind the sheet.
  useEffect(() => {
    if (!sheetVisible) return undefined;
    const { body } = document;
    const prior = body.style.overflow;
    body.style.overflow = 'hidden';
    return () => {
      body.style.overflow = prior;
    };
  }, [sheetVisible]);

  // A standalone menu focuses the list itself (not an item) so the first Arrow key lands
  // on the first/last item — however it was opened (mouse or keyboard). A dialog panel takes focus
  // too, so the flow continues inside it and Escape is heard wherever it was opened from.
  useEffect(() => {
    if (!open || !(role === 'menu' || asDialog)) return;
    if (!asSheet && !position.ready) return;
    if (asDialog) {
      panelRef.current?.focus();
      return;
    }
    panelRef.current?.querySelector<HTMLElement>('[role="menu"]')?.focus();
  }, [open, role, position.ready, asSheet, asDialog]);

  // Roving focus for a standalone `menu` (a `listbox` is driven by its combobox trigger).
  const menuItems = (): HTMLElement[] =>
    Array.from(
      panelRef.current?.querySelectorAll<HTMLElement>(
        '[role="menuitem"]:not([aria-disabled="true"])',
      ) ?? [],
    );

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (role !== 'menu') return;
    const items = menuItems();
    if (items.length === 0) return;
    const current = items.indexOf(document.activeElement as HTMLElement);
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        items[current < 0 ? 0 : (current + 1) % items.length]?.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        items[
          current < 0 ? items.length - 1 : (current - 1 + items.length) % items.length
        ]?.focus();
        break;
      case 'Home':
        event.preventDefault();
        items[0]?.focus();
        break;
      case 'End':
        event.preventDefault();
        items[items.length - 1]?.focus();
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        (document.activeElement as HTMLElement | null)?.click();
        break;
      case 'Tab':
      case 'Escape':
        event.preventDefault();
        onClose();
        returnFocus();
        break;
      default:
        break;
    }
  };

  if (typeof document === 'undefined' || (!sheetVisible && !dropdownVisible)) return null;

  // The panel is portaled to <body>, so it escapes a `data-theme` set on an ancestor of
  // the trigger. Copy that theme onto the panel so its tokens resolve for the right scheme.
  const themeAttr =
    anchorRef.current?.closest('[data-theme]')?.getAttribute('data-theme') ?? undefined;

  // For `dialog` the role and the accessible name belong to the panel rather than to the scroll
  // container: the panel is the dialog, the inner div only scrolls. It is deliberately non-modal —
  // focus is not trapped, so `aria-modal` stays off.
  const panelAria = asDialog ? { ...aria, role: 'dialog' as const, tabIndex: -1 } : {};

  const body = (
    <>
      {header != null && <div className={styles.header}>{header}</div>}
      {/* Composite widget (listbox/menu): it owns roving-focus key handling for its options. */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        {...(asDialog ? {} : aria)}
        ref={listRef}
        role={asDialog ? undefined : role}
        id={id}
        tabIndex={role === 'menu' ? -1 : undefined}
        className={classNames(styles.list, asSheet && styles.listSheet)}
        style={asSheet ? undefined : { maxHeight: `${String(maxHeight)}px` }}
        onKeyDown={handleKeyDown}
      >
        {children}
      </div>
      {footer != null && <div className={styles.footer}>{footer}</div>}
    </>
  );

  if (sheetVisible) {
    const closing = phase === 'closing';
    return createPortal(
      <div className={styles.sheetRoot} data-theme={themeAttr}>
        <div
          className={classNames(styles.overlay, closing && styles.overlayClosing)}
          data-menu-overlay
          aria-hidden="true"
          onClick={onClose}
        />
        <div
          {...panelAria}
          ref={panelRef}
          data-menu-sheet
          className={classNames(styles.sheet, closing && styles.sheetClosing, className)}
        >
          {body}
        </div>
      </div>,
      document.body,
    );
  }

  const style: CSSProperties = {
    top: `${String(position.top)}px`,
    left: `${String(position.left)}px`,
    width: position.width != null ? `${String(position.width)}px` : undefined,
    visibility: position.ready ? 'visible' : 'hidden',
  };

  return createPortal(
    <div
      {...panelAria}
      ref={panelRef}
      data-theme={themeAttr}
      className={classNames(styles.panel, className)}
      style={style}
    >
      {body}
    </div>,
    document.body,
  );
}
